import { transformOfferToFulfillmentWindowFormValue, validTimezones } from '@occc/feat-constraints';
import { v4 as uuid } from 'uuid';

import { OfferFragment, Random_Award_Type } from '@occc/graphql';
import { earningMechanismHelper } from '@occc-app/offerHelpers/utils/earningMechanismHelper';
import { transformCustomData } from '@occc-app/routes/CampaignComponents/components/CustomData/utils';

import { getOfferNameAndDescriptionInitialValues } from '@occc-app/routes/Offer/components/OfferConfigureForm/utils/offerNameAndDescriptionInitialValues';
import { getOfferFulfillmentPriorityInitialValues } from '@occc-app/offerHelpers/utils/getOfferFulfillmentPriorityInitialValues';
import { LimitTimePeriod, LimitType, transformOfferToLimitFormValues } from '@occc/feat-limits';
import { GameConfigureFormValues, GameType, SpinWheelGameOfferCustomData } from '../../types';
import { getDefaultSegments, INITIAL_SEGMENTS_LENGTH } from '../constants';

export const getConfigureFormInitialValues = (): GameConfigureFormValues => ({
  gameType: GameType.SPIN_THE_WHEEL,
  purchasableOffer: null,
  segments: getDefaultSegments(INITIAL_SEGMENTS_LENGTH),
  fulfillmentWindow: { isEnabled: false, timezone: validTimezones[0] },
  awardCount: {
    membership: { isEnabled: false, timePeriod: LimitTimePeriod.Day },
    global: { timePeriod: LimitTimePeriod.Day },
  },
  spinWheelGameConfig: {
    headerText: null,
    headerTextColor: null,
    footerTextColor: null,
    borderColor: null,
    backgroundPrimaryColor: null,
    backgroundSecondaryColor: null,
    backgroundImage: null,
    foregroundImage: null,
    buttonColor: null,
    buttonTextColor: null,
  },
  name: '',
  description: '',
});

export const getSpinWheelGameInitialValues = (offer: OfferFragment): GameConfigureFormValues => {
  // TODO find better solution
  const { earningMechanism } = offer;
  if (!earningMechanism || !earningMechanismHelper.isSpinWheelGame(earningMechanism)) {
    return getConfigureFormInitialValues();
  }

  const spinWheelGameOfferCustomData: SpinWheelGameOfferCustomData | null =
    // TODO remove ts-ignore , add validation
    // @ts-ignore
    offer.customData?.spinWheelGame || null;

  return {
    gameType: GameType.SPIN_THE_WHEEL,
    purchasableOffer: earningMechanism.purchasableOffer
      ? { id: earningMechanism.purchasableOffer.id, name: earningMechanism.purchasableOffer.name }
      : null,
    segments:
      earningMechanism.awards.map((award, index) => {
        const customDataSegment = spinWheelGameOfferCustomData?.segments[index];
        return {
          id: uuid(),
          type: award.awardType,
          awardPoints:
            award.awardType === Random_Award_Type.Points &&
            award.award?.__typename === 'AwardPoints'
              ? award.award.points
              : null,
          limit: award.limit,
          color: customDataSegment?.color || null,
          icon: customDataSegment?.icon || null,
          awardOffer:
            award.awardType === Random_Award_Type.Vouchers &&
            award.award?.__typename === 'AwardVouchers'
              ? { id: award.award.voucheredOffer.id, name: award.award.voucheredOffer.name }
              : null,
          awardOfferExpirationDays:
            (award.awardType === Random_Award_Type.Vouchers &&
              award.award?.__typename === 'AwardVouchers' &&
              award.award.voucherExpirationDays) ||
            null,
          awardOfferVoucherCount:
            (award.awardType === Random_Award_Type.Vouchers &&
              award.award?.__typename === 'AwardVouchers' &&
              award.award.voucherCount) ||
            null,
        };
      }) || [],
    fulfillmentWindow: { isEnabled: false, timezone: validTimezones[0] },
    spinWheelGameConfig: spinWheelGameOfferCustomData
      ? {
          headerText: spinWheelGameOfferCustomData.headerText
            ? spinWheelGameOfferCustomData.headerText.trim()
            : null,
          headerTextColor: spinWheelGameOfferCustomData.headerTextColor,
          footerTextColor: spinWheelGameOfferCustomData.footerTextColor,
          borderColor: spinWheelGameOfferCustomData.borderColor,
          backgroundPrimaryColor: spinWheelGameOfferCustomData.backgroundPrimaryColor,
          backgroundSecondaryColor: spinWheelGameOfferCustomData.backgroundSecondaryColor,
          backgroundImage: spinWheelGameOfferCustomData.backgroundImage,
          foregroundImage: spinWheelGameOfferCustomData.foregroundImage,
          buttonColor: spinWheelGameOfferCustomData.buttonColor,
          buttonTextColor: spinWheelGameOfferCustomData.buttonTextColor,
        }
      : {
          headerText: null,
          headerTextColor: null,
          footerTextColor: null,
          borderColor: null,
          backgroundPrimaryColor: null,
          backgroundSecondaryColor: null,
          backgroundImage: null,
          foregroundImage: null,
          buttonColor: null,
          buttonTextColor: null,
        },
    awardCount: transformOfferToLimitFormValues(offer, LimitType.AwardCount),
    customData: transformCustomData(offer.customData),
    ...transformOfferToFulfillmentWindowFormValue(offer),
    ...getOfferNameAndDescriptionInitialValues(offer),
    ...getOfferFulfillmentPriorityInitialValues(offer),
  };
};

export const transformOfferToInitialValues = (offer?: OfferFragment): GameConfigureFormValues => {
  const defaultValues = getConfigureFormInitialValues();

  if (!offer?.earningMechanism) {
    return defaultValues;
  }

  if (earningMechanismHelper.isSpinWheelGame(offer.earningMechanism)) {
    return getSpinWheelGameInitialValues(offer);
  }

  return defaultValues;
};
